import { ReactNode, useCallback, useState } from 'react';
import {
  InputAdornment,
  TextField as MuiTextField,
  TextFieldProps as MuiTextFieldProps,
} from '@mui/material';

import { LabelPanel } from './LabelPanel';

export type TextFieldProps = MuiTextFieldProps & {
  endLabel?: ReactNode;
  hasCounter?: boolean;
  maxLength?: number;
  shouldKeepExtraSpaceForHelperText?: boolean;
};

const getOffset = (value: unknown): number =>
  typeof value === 'string' ? value.length : 0;

export function TextField({
  label,
  endLabel,
  disabled,
  hasCounter,
  shouldKeepExtraSpaceForHelperText = false,
  // initialized with empty space to prevent blinking
  // when helperText has appeared
  helperText = shouldKeepExtraSpaceForHelperText ? ' ' : undefined,
  maxLength,
  onChange: handleChange,
  ...props
}: TextFieldProps) {
  const [offset, setOffset] = useState(getOffset(props.value));

  const onChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const currentOffset = getOffset(event.target.value);

      const isLimitReached = maxLength && currentOffset > maxLength;

      if (!isLimitReached) {
        setOffset(getOffset(event.target.value));
      }

      handleChange?.(event);
    },
    [handleChange, maxLength],
  );

  const isLabel = Boolean(label || endLabel);

  const endAdornment = hasCounter && props.multiline && Boolean(maxLength) && (
    <InputAdornment position="end">
      {offset}/{maxLength}
    </InputAdornment>
  );

  return (
    <MuiTextField
      InputProps={{ ...props.InputProps, endAdornment }}
      // TextField component accepts two different sets of props:
      // - InputProps - props applied to the Input component
      // - inputProps - props passed directly to the html element
      // They are different and written with different names but
      // Eslint considers them (names) like identical.
      //
      // eslint-disable-next-line react/jsx-no-duplicate-props
      inputProps={{ ...props.inputProps, maxLength }}
      disabled={disabled}
      label={
        isLabel ? (
          <LabelPanel label={label} endLabel={endLabel} disabled={disabled} />
        ) : undefined
      }
      helperText={helperText}
      onChange={onChange}
      {...props}
    />
  );
}
